import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Loader2, Trash2, RefreshCw, User, Calendar, CheckCircle, XCircle, Clock } from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const getErrorMessage = (err) => {
    try {
        const detail = err?.response?.data?.detail;
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail)) return detail.map(e => e?.msg || 'Error').join('. ');
        return err?.message || 'An error occurred';
    } catch { return 'An error occurred'; }
};

const AdminDeletions = () => {
    const [loading, setLoading] = useState(true);
    const [requests, setRequests] = useState([]);
    const [actionDialog, setActionDialog] = useState({ open: false, action: '', request: null });
    const [processing, setProcessing] = useState(false);

    useEffect(() => { loadRequests(); }, []);

    const loadRequests = async () => {
        setLoading(true);
        try {
            const res = await adminAPI.getDeletionRequests();
            setRequests(res.data || []);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    const handleAction = async (action, request) => {
        setProcessing(true);
        try {
            await adminAPI.handleDeletionRequest(request.id, action);
            toast.success(`Deletion request ${action}`);
            loadRequests();
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setProcessing(false);
            setActionDialog({ open: false, action: '', request: null });
        }
    };

    const pendingRequests = requests.filter(r => r.status === 'pending');
    const processedRequests = requests.filter(r => r.status !== 'pending');

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="p-6 lg:p-8 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="font-heading font-extrabold text-2xl lg:text-3xl text-foreground">Deletion Requests</h1>
                        <p className="text-muted-foreground">Review account deletion requests</p>
                    </div>
                    <Button onClick={loadRequests} variant="outline" size="sm">
                        <RefreshCw className="h-4 w-4 mr-2" /> Refresh
                    </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                    <Card className="bg-amber-500/10 border-amber-500/30">
                        <CardContent className="p-4 text-center">
                            <Clock className="h-6 w-6 text-amber-500 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-amber-500">{pendingRequests.length}</p>
                            <p className="text-sm text-muted-foreground">Pending</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-emerald-500/10 border-emerald-500/30">
                        <CardContent className="p-4 text-center">
                            <CheckCircle className="h-6 w-6 text-emerald-500 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-emerald-500">{requests.filter(r => r.status === 'approved').length}</p>
                            <p className="text-sm text-muted-foreground">Approved</p>
                        </CardContent>
                    </Card>
                    <Card className="bg-red-500/10 border-red-500/30">
                        <CardContent className="p-4 text-center">
                            <XCircle className="h-6 w-6 text-red-500 mx-auto mb-2" />
                            <p className="text-2xl font-bold text-red-500">{requests.filter(r => r.status === 'rejected').length}</p>
                            <p className="text-sm text-muted-foreground">Rejected</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Pending Requests */}
                <Card className="bg-card border-border">
                    <CardHeader>
                        <CardTitle className="font-heading font-bold text-lg flex items-center gap-2">
                            <Clock className="h-5 w-5 text-amber-500" />
                            Pending Requests ({pendingRequests.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {pendingRequests.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                                <p>No pending deletion requests</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {pendingRequests.map((req) => (
                                    <div key={req.id} className="p-4 bg-amber-500/5 rounded-lg border border-amber-500/30">
                                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                                            <div className="flex-1 min-w-0 space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <User className="h-4 w-4 text-muted-foreground" />
                                                    <p className="font-medium text-foreground">{req.company_name || 'Unknown Company'}</p>
                                                    <Badge className="bg-amber-600">Pending</Badge>
                                                </div>
                                                <p className="text-sm text-muted-foreground">{req.user_email}</p>
                                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                    <Calendar className="h-3 w-3" />
                                                    Requested: {formatDate(req.requested_at)}
                                                </div>
                                                {req.reason && (
                                                    <div className="p-3 bg-background rounded border border-border mt-2">
                                                        <p className="text-xs text-muted-foreground mb-1">Reason:</p>
                                                        <p className="text-sm text-foreground">"{req.reason}"</p>
                                                    </div>
                                                )}
                                                <p className="text-xs text-muted-foreground">
                                                    Reviews submitted: {req.reviews_count || 0}
                                                </p>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                                                    onClick={() => setActionDialog({ open: true, action: 'approved', request: req })}>
                                                    <CheckCircle className="h-4 w-4 mr-1" /> Approve
                                                </Button>
                                                <Button size="sm" variant="destructive"
                                                    onClick={() => setActionDialog({ open: true, action: 'rejected', request: req })}>
                                                    <XCircle className="h-4 w-4 mr-1" /> Reject
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Processed Requests */}
                {processedRequests.length > 0 && (
                    <Card className="bg-card border-border">
                        <CardHeader>
                            <CardTitle className="font-heading font-bold text-lg">
                                Processed Requests ({processedRequests.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3 max-h-[400px] overflow-y-auto">
                                {processedRequests.map((req) => (
                                    <div key={req.id} className="flex items-center justify-between p-3 bg-accent/30 rounded-lg border border-border">
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-foreground truncate">{req.company_name || req.user_email}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {formatDate(req.requested_at)}
                                                {req.reviewed_at && ` • Reviewed: ${formatDate(req.reviewed_at)}`}
                                            </p>
                                        </div>
                                        <Badge className={req.status === 'approved' ? 'bg-emerald-600' : 
                                                         req.status === 'rejected' ? 'bg-red-600' : 'bg-gray-600'}>
                                            {req.status}
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Action Dialog */}
            <AlertDialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, action: '', request: null })}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {actionDialog.action === 'approved' ? 'Approve Deletion Request' : 'Reject Deletion Request'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {actionDialog.action === 'approved' 
                                ? 'This will mark the deletion request as approved. Note: Account data may still be retained for audit and compliance purposes.'
                                : 'This will reject the deletion request. The user account will remain deactivated.'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={processing}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleAction(actionDialog.action, actionDialog.request)}
                            disabled={processing}
                            className={actionDialog.action === 'rejected' ? 'bg-destructive hover:bg-destructive/90' : 'bg-emerald-600 hover:bg-emerald-700'}>
                            {processing ? 'Processing...' : actionDialog.action === 'approved' ? 'Approve' : 'Reject'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AdminLayout>
    );
};

export default AdminDeletions;
